import { DialogElement } from "theme";
import { cartAdapter, readConfig } from "ds-cart-ajax";
import {
  cartAmounts, shippingFor, giftChoices, stepEligible, selectedGifts, codeKey, discountAmount, progressState, subtotalVisible
} from "ds-cart-model";

const element = (tag, attributes = {}, children = []) => {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) {
    if (value !== null && value !== false) node.setAttribute(key, value === true ? "" : String(value));
  }
  node.append(...children.filter((child) => child != null));
  return node;
};
const safeURL = (value) => {
  if (!value) return "";
  try {
    const url = new URL(value, window.location.origin);
    return ["https:", "http:"].includes(url.protocol) ? url.href : "";
  } catch { return ""; }
};

class DSCartDrawer extends DialogElement {
  get shouldLock() { return true; }

  connectedCallback() {
    super.connectedCallback();
    this.life?.abort();
    this.life = new AbortController();
    this.drafts = this.drafts || { choices: {} };
    this.recommendations = this.recommendations || new Map();
    const options = { signal: this.life.signal };
    this.addEventListener("click", (event) => this.onClick(event), options);
    this.addEventListener("change", (event) => this.onChange(event), options);
    this.addEventListener("submit", (event) => this.onSubmit(event), options);
    this.addEventListener("pointerover", (event) => {
      const marker = event.target.closest("[data-ds-reward]");
      if (event.pointerType === "mouse" && marker && !marker.contains(event.relatedTarget)) this.showReward(marker);
    }, options);
    this.addEventListener("pointerout", (event) => {
      const marker = event.target.closest("[data-ds-reward]");
      if (event.pointerType === "mouse" && marker && !marker.contains(event.relatedTarget) && !marker.contains(document.activeElement)) this.closeReward();
    }, options);
    this.addEventListener("focusin", (event) => {
      const marker = event.target.closest("[data-ds-reward]");
      // Touch focus is followed by click, which owns the mobile toggle.
      if (marker && event.target.matches(":focus-visible")) this.showReward(marker);
    }, options);
    this.addEventListener("focusout", (event) => {
      const marker = event.target.closest("[data-ds-reward]");
      if (marker && !marker.contains(event.relatedTarget)) this.closeReward();
    }, options);
    // Intercept before the dialog's document-level focus trap closes the drawer.
    window.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.activeReward) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.closeReward();
      }
    }, { ...options, capture: true });
    document.addEventListener("pointerdown", (event) => {
      if (this.activeReward && !this.activeReward.contains(event.target)) this.closeReward();
    }, options);
    window.addEventListener("resize", () => this.closeReward(), options);
    document.addEventListener("liquid-ajax-cart:request-start", () => this.snapshot(), options);
    document.addEventListener("ds-cart:render", () => this.render(), options);
    document.addEventListener("ds-cart:busy", () => this.busy(), options);
    document.addEventListener("ds-cart:warning", (event) => this.error(event.detail.message), options);
    this.addEventListener("dialog:after-hide", (event) => {
      if (event.target !== this) return;
      this.closeReward();
      this.querySelectorAll("dialog-element[open]").forEach((modal) => modal.hide());
      const target = this.returnFocusTo;
      this.returnFocusTo = null;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.open && target?.isConnected && target.getClientRects().length) target.focus({ preventScroll: true });
      }));
    }, options);
    cartAdapter.ready.then(() => { if (this.isConnected) this.render(); });
    this.openFromUrl();
  }

  // /cart redirects here with ?open_cart=1; drop the flag so reloads stay closed.
  openFromUrl() {
    const url = new URL(location.href);
    if (url.searchParams.get("open_cart") !== "1") return;
    url.searchParams.delete("open_cart");
    history.replaceState(history.state, "", url.pathname + url.search + url.hash);
    this.show();
  }

  disconnectedCallback() {
    this.closeReward();
    this.life?.abort();
    this.recommendationRequest?.abort();
    super.disconnectedCallback();
  }

  show(animate = true) {
    if (!this.open && !this.returnFocusTo) this.returnFocusTo = document.activeElement;
    return super.show(animate);
  }

  _showTransition(animate = true) {
    this.removeAttribute("data-closing");
    this.slide?.cancel();
    const duration = animate && !matchMedia("(prefers-reduced-motion: reduce)").matches ? 280 : 0;
    this.slide = this.querySelector(".ds-drawer__inner").animate(
      { transform: ["translateX(100%)", "translateX(0)"] }, { duration, easing: "cubic-bezier(.2,.7,.2,1)" }
    );
    return this.slide.finished.catch(() => {});
  }

  _hideTransition() {
    this.setAttribute("data-closing", "");
    this.slide?.cancel();
    const duration = matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 220;
    this.slide = this.querySelector(".ds-drawer__inner").animate(
      { transform: ["translateX(0)", "translateX(100%)"] }, { duration, easing: "ease-in" }
    );
    return this.slide.finished.catch(() => {}).then(() => {
      this.removeAttribute("data-closing");
    });
  }

  money(amount) {
    const config = this.config || readConfig();
    return new Intl.NumberFormat(config.locale || "de", { style: "currency", currency: cartAdapter.cart?.currency || config.currency || "EUR" }).format(amount / 100);
  }

  error(message = "") {
    const node = this.querySelector("[data-ds-error]");
    if (node) { node.textContent = message; node.hidden = !message; }
  }

  async perform(operation) {
    this.error();
    try {
      await operation();
      const status = this.querySelector("[data-ds-status]");
      if (status) status.textContent = this.config.strings.updated;
    } catch (error) {
      this.error(error.message || this.config.strings.error);
    }
  }

  busy() {
    const busy = cartAdapter.pending > 0;
    this.setAttribute("aria-busy", String(busy));
    this.querySelectorAll("[data-ds-checkout] button").forEach((button) => { button.disabled = busy; });
    this.querySelectorAll("[data-ds-discount] button").forEach((button) => {
      button.disabled = busy;
      button.setAttribute("aria-busy", String(busy));
    });
  }

  snapshot() {
    if (!this.drafts) return;
    const focused = document.activeElement;
    if (this.contains(focused)) {
      this.drafts.focus = focused.dataset.focusKey || null;
      this.drafts.focusNode = focused;
      this.drafts.focusIndex = [...this.querySelectorAll("[data-ds-item]")].indexOf(focused.closest("[data-ds-item]"));
    } else {
      this.drafts.focus = null;
    }
    for (const key of ["discount", "note"]) {
      const input = this.querySelector('[data-focus-key="' + key + '"]');
      if (input) this.drafts[key] = input.value;
    }
    this.drafts.modals = [...this.querySelectorAll("dialog-element[open]")].map((modal) => modal.id);
  }

  restore() {
    for (const key of ["discount", "note"]) {
      const input = this.querySelector('[data-focus-key="' + key + '"]');
      if (input && this.drafts[key] !== undefined) input.value = this.drafts[key];
    }
    if (this.open) {
      for (const id of this.drafts.modals || []) this.querySelector("#" + CSS.escape(id))?.show(false);
      if (this.drafts.focus && !this.drafts.focusNode?.isConnected) {
        const focus = this.querySelector('[data-focus-key="' + CSS.escape(this.drafts.focus) + '"]') ||
          this.querySelectorAll("[data-ds-item]")[Math.max(0, this.drafts.focusIndex)]?.querySelector("button") ||
          this.querySelector("[data-ds-close][id]");
        // Wait for the old nested dialog's focus return before restoring a new input.
        requestAnimationFrame(() => { if (this.open) focus?.focus({ preventScroll: true }); });
      }
    }
  }

  render() {
    if (!cartAdapter.cart) return;
    this.config = readConfig();
    const cart = cartAdapter.cart, campaign = cartAdapter.campaign;
    const amounts = cartAmounts(cart, this.config.lines);
    const shipping = shippingFor(cart, campaign, this.config);
    const subtotal = this.querySelector("[data-ds-subtotal]");
    if (subtotal) subtotal.textContent = this.money(amounts.subtotal);
    const subtotalRow = this.querySelector("[data-ds-subtotal-row]");
    if (subtotalRow) subtotalRow.hidden = !subtotalVisible(this.config.subtotalShipping, window.Shopify?.currency?.rate);
    const total = this.querySelector("[data-ds-total]");
    if (total) total.textContent = this.money(amounts.subtotal + shipping.cost);
    const checkoutTotal = this.querySelector("[data-ds-checkout-total]");
    if (checkoutTotal) checkoutTotal.textContent = " · " + this.money(amounts.subtotal + shipping.cost);
    const savings = this.querySelector("[data-ds-savings]");
    if (savings) {
      savings.textContent = this.money(amounts.savings);
      this.querySelector("[data-ds-savings-row]").hidden = amounts.savings <= 0;
    }
    const shippingNode = this.querySelector("[data-ds-shipping]");
    if (shippingNode) {
      shippingNode.textContent = shipping.cost ? this.money(shipping.cost) : this.config.strings.free;
      shippingNode.dataset.free = String(shipping.cost === 0);
      this.querySelector("[data-ds-shipping-row]").hidden = cart.requires_shipping === false || !shipping.known;
    }
    this.renderProgress(cart, campaign, amounts, shipping);
    this.renderGifts(cart, campaign);
    this.renderCodes(cart);
    // Automatic gifts cannot be removed while their conditions remain fulfilled.
    this.querySelectorAll("[data-gift-step]").forEach((row) => {
      const automatic = campaign?.steps?.some((step) => String(step.id) === row.dataset.giftStep && step.type === "automatic_gift");
      const remove = row.querySelector("[data-ds-remove]");
      if (remove) remove.hidden = automatic;
    });
    this.restore();
    this.renderDiscountFeedback();
    this.busy();
    this.loadRecommendations(cart).catch(() => {}); // Manual bedrop products remain on failure.
  }

  progressIcon(type) {
    const name = ["shipping", "truck", "volume_discount", "chevron"].includes(type) ? type : "gift";
    return this.querySelector(`[data-ds-progress-icon="${name}"]`)?.content.cloneNode(true);
  }

  closeReward() {
    if (!this.activeReward) return;
    this.activeReward.querySelector("[data-ds-reward-toggle]").setAttribute("aria-expanded", "false");
    this.activeReward.querySelector("[role='tooltip']").hidden = true;
    this.activeReward.classList.remove("is-open");
    this.activeReward = null;
  }

  showReward(marker) {
    this.closeReward();
    const tooltip = marker.querySelector("[role='tooltip']");
    const track = marker.closest(".ds-progress-bar__track-container").getBoundingClientRect();
    const bounds = marker.getBoundingClientRect();
    const width = Math.min(300, track.width);
    const left = Math.max(track.left, Math.min(bounds.left + bounds.width / 2 - width / 2, track.right - width));
    tooltip.style.width = `${width}px`;
    tooltip.style.left = `${left - bounds.left}px`;
    tooltip.hidden = false;
    marker.querySelector("[data-ds-reward-toggle]").setAttribute("aria-expanded", "true");
    marker.classList.add("is-open");
    this.activeReward = marker;
  }

  progressProducts(step) {
    return [...new Map([...(step.gift_products || []), ...(step.gift_variants || [])]
      .map((product) => [product.variant_id || product.id, product])).values()];
  }

  appendProgressProducts(target, products) {
    products.forEach((product, index) => {
      if (index) target.append(element("div", { class: "ds-progress-bar__product-separator" }, [this.config.strings.or]));
      const variant = product.variants?.find((entry) => String(entry.id) === String(product.variant_id));
      target.append(element("div", { class: "ds-progress-bar__product-preview" }, [
        safeURL(product.featured_image) ? element("img", { class: "ds-progress-bar__product-image", src: safeURL(product.featured_image), alt: "", width: 44, height: 44 }) : null,
        element("div", { class: "ds-progress-bar__product-info" }, [
          element("div", { class: "ds-progress-bar__product-title" }, [product.title]),
          variant?.title && variant.title !== "Default Title" ? element("div", { class: "ds-progress-bar__product-subtitle" }, [variant.title]) : null
        ])
      ]));
    });
  }

  renderProgress(cart, campaign) {
    const target = this.querySelector("[data-ds-progress]");
    if (!target) return;
    this.closeReward();
    const state = progressState(cart, campaign, this.config);
    const { steps, next, complete, progress, markers } = state;
    target.hidden = !steps.length;
    target.replaceChildren();
    if (!steps.length) return;
    if (this.config.progressStyle === "next") {
      target.append(this.nextReward(cart, campaign, state));
      return;
    }
    const strings = this.config.strings;
    const remaining = Math.max(0, (next?.amount || 0) - (next?.current || 0));
    const reward = next?.incentive_text || next?.title || strings.giftRequirements;
    const text = complete ? strings.progressReached : remaining > 0
      ? strings.progressReward.replace("__amount__", this.money(remaining)).replace("__title__", reward)
      : (next?.incentive_text || strings.giftRequirements);
    const track = element("div", { class: "ds-progress-bar__track", role: "progressbar", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": Math.round(progress), "aria-label": text }, [
      element("div", { class: "ds-progress-bar__fill", style: `width:${progress}%` })
    ]);
    const markerList = element("div", { class: "ds-progress-bar__markers" });
    if (this.config.showProgressSteps && this.config.giftDisplayType !== "list") {
      for (const step of markers) {
        const label = new Intl.NumberFormat(this.config.locale || "de", {
          style: "currency", currency: cart.currency || "EUR", minimumFractionDigits: step.amount % 100 === 0 ? 0 : 2
        }).format(step.amount / 100);
        const tooltipId = `${this.id}-reward-${markerList.childElementCount}`;
        const title = (step.type === "shipping" ? strings.shippingTooltip : ["automatic_gift", "selectable_gifts"].includes(step.type)
          ? strings.giftTooltip : strings.rewardTooltip).replace("__amount__", this.money(step.amount)).replace("__title__", step.title || "");
        const tooltip = element("div", { id: tooltipId, class: "ds-progress-bar__tooltip", role: "tooltip", hidden: true }, [
          element("div", { class: "ds-progress-bar__tooltip-title" }, [title])
        ]);
        this.appendProgressProducts(tooltip, this.progressProducts(step));
        if (step.incentive_text) tooltip.append(element("p", { class: "ds-progress-bar__tooltip-note" }, [step.incentive_text]));
        markerList.append(element("div", {
          class: "ds-progress-bar__step-marker" + (step.reached ? " completed" : ""),
          style: `left:${step.position}%`, "data-ds-reward": "",
          "data-reached": String(step.reached)
        }, [
          element("button", { class: "ds-progress-bar__marker-button", type: "button", "data-ds-reward-toggle": "",
            "data-focus-key": `reward-${step.type}-${step.id}`, "aria-label": title, "aria-describedby": tooltipId, "aria-controls": tooltipId, "aria-expanded": "false"
          }, [element("span", { class: "ds-progress-bar__marker-icon-container", "aria-hidden": "true" }, [this.progressIcon(step.type)])]),
          step.showAmount && this.config.showShippingAmounts ? element("div", { class: "ds-progress-bar__step-price", "aria-hidden": "true" }, [label]) : null,
          tooltip
        ]));
      }
    }
    const bar = element("div", { class: "ds-progress-bar" }, [
      element("div", { class: "ds-progress-bar__track-container" }, [track, markerList])
    ]);
    // Completion is represented by the filled markers, not a second message.
    if (!complete) {
      const products = this.progressProducts(next);
      const messageParts = remaining > 0 ? strings.progressReward.split(/(__amount__|__title__)/).map((part) =>
        part === "__amount__" ? element("strong", {}, [this.money(remaining)]) : part === "__title__" ? element("strong", {}, [reward]) : part
      ) : [text];
      const detailsId = `${this.id}-progress-details`;
      const expanded = Boolean(products.length && this.drafts.progressExpanded);
      const toggle = element(products.length ? "button" : "div", {
        class: "ds-progress-bar__message-toggle", type: products.length ? "button" : null,
        "data-ds-progress-toggle": products.length || null,
        "data-focus-key": products.length ? "progress-details" : null,
        "aria-expanded": products.length ? String(expanded) : null,
        "aria-controls": products.length ? detailsId : null
      }, [element("span", { class: "ds-progress-bar__text" }, messageParts),
        products.length ? element("span", { class: "ds-progress-bar__chevron" }, [this.progressIcon("chevron")]) : null]);
      const details = element("div", { id: detailsId, class: "ds-progress-bar__message-details", hidden: !expanded });
      this.appendProgressProducts(details, products);
      bar.append(element("div", { class: "ds-progress-bar__message" }, [toggle, details]));
    }
    if (this.config.showProgressSteps && this.config.giftDisplayType === "list") {
      const list = element("div", { class: "ds-upcoming-gifts-list" });
      for (const step of steps) {
        if (step.reached && step.type !== "selectable_gifts") continue;
        const choices = giftChoices(step).filter((choice) => !cart.items.some((item) => String(item.variant_id) === String(choice.variant_id)));
        if (!choices.length) continue;
        list.append(element("div", { class: "ds-step-gift-group" }, [
          element("div", { class: "ds-step-gift-group__header" }, [step.title, " · ", this.money(step.amount)]),
          element("div", { class: "ds-step-gift-group__progress-track" }, [element("div", { class: "ds-step-gift-group__progress-fill", style: `width:${step.amount > 0 ? Math.min(100, step.current / step.amount * 100) : 100}%` })]),
          element("div", { class: "ds-step-gift-group__items" }, choices.map((choice) => element("span", {
            class: "ds-upcoming-gift-item" + (step.reached ? " unlocked" : " locked"),
            title: choice.title
          }, [safeURL(choice.featured_image) ? element("img", { src: safeURL(choice.featured_image), alt: choice.title, width: 40, height: 40 }) : choice.title])))
        ]));
      }
      bar.append(list);
    }
    target.append(bar);
  }

  // Compact alternative to the multi-step bar: only the next reachable reward.
  nextReward(cart, campaign, { steps, next, complete, progress }) {
    const strings = this.config.strings;
    const remaining = Math.max(0, (next.amount || 0) - (next.current || 0));
    const kind = next.type === "shipping" ? "shipping" : ["automatic_gift", "selectable_gifts"].includes(next.type) ? "gift" : "reward";
    const hasReachedGift = steps.some((step) => step.reached && ["automatic_gift", "selectable_gifts"].includes(step.type));
    const giftRemaining = hasReachedGift ? strings.nextGiftAfterFirstRemaining || strings.nextGiftRemaining : strings.nextGiftRemaining;
    const fill = (template) => template.split(/(__amount__|__title__)/).map((part) =>
      part === "__amount__" ? element("strong", { class: "ds-next-reward__amount" }, [this.money(remaining)])
        : part === "__title__" ? next.title || "" : part);
    let title, subtext = "";
    if (complete) {
      title = [steps.every((step) => step.type === "shipping") ? strings.nextShippingReached : strings.nextAllReached];
    } else if (remaining > 0) {
      title = fill({ shipping: strings.nextShippingRemaining, gift: giftRemaining, reward: strings.nextRewardRemaining }[kind]);
      const standardCost = shippingFor(cart, campaign, this.config).standardCost;
      subtext = kind === "shipping" ? (standardCost > 0 ? strings.nextShippingSubtext.replace("__amount__", this.money(standardCost)) : "")
        : next.incentive_text || this.progressProducts(next).map((product) => product.title).join(` ${strings.or} `);
    } else {
      title = [next.incentive_text || strings.giftRequirements];
    }
    const label = title.map((part) => typeof part === "string" ? part : part.textContent).join("");
    return element("div", { class: "ds-next-reward", "data-type": next.type, "data-reached": String(complete) }, [
      element("div", { class: "ds-next-reward__head" }, [
        element("span", { class: "ds-next-reward__icon", "aria-hidden": "true" }, [this.progressIcon(kind === "shipping" ? "truck" : next.type)]),
        element("div", { class: "ds-next-reward__text" }, [
          element("p", { class: "ds-next-reward__title" }, title),
          subtext ? element("p", { class: "ds-next-reward__subtext" }, [subtext]) : null
        ])
      ]),
      element("div", { class: "ds-next-reward__track", role: "progressbar", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": Math.round(progress), "aria-label": label }, [
        element("div", { class: "ds-next-reward__fill", style: `width:${progress}%` })
      ]),
      this.config.showShippingAmounts && next.amount > 0 ? element("div", { class: "ds-next-reward__target" }, [this.money(next.amount)]) : null
    ]);
  }

  renderGifts(cart, campaign) {
    const target = this.querySelector("[data-ds-gifts]");
    if (!target) return;
    const steps = (campaign?.steps || []).filter((step) => step.type === "selectable_gifts" && stepEligible(step, cart, this.config.lines));
    target.hidden = !steps.length;
    const strings = this.config.strings;
    target.replaceChildren(...steps.map((step) => {
      const selected = new Set(selectedGifts(cart, step).map((item) => String(item.variant_id)));
      const limit = Math.max(0, Number(step.gift_selection_limit || 1) - selected.size);
      const choices = giftChoices(step).filter((gift) => !selected.has(String(gift.variant_id)));
      const availableIds = new Set(choices.map((choice) => String(choice.variant_id)));
      const selection = new Set((this.drafts.choices[step.id] || []).filter((id) => availableIds.has(id)).slice(0, limit));
      this.drafts.choices[step.id] = [...selection];
      const form = element("form", { class: "ds-gift-tier", "data-ds-gift-form": step.id }, [
        element("h3", {}, [step.title]),
        element("p", {}, [limit ? strings.chooseGifts.replace("__count__", String(limit)) : strings.giftSelected])
      ]);
      if (!choices.length && limit) form.append(element("p", {}, [strings.giftUnavailable]));
      if (choices.length && limit) {
        form.append(element("div", { class: "ds-gift-tier__choices" }, choices.map((gift) =>
          element("label", { class: "ds-gift-choice" }, [
            element("input", {
              type: limit === 1 ? "radio" : "checkbox", name: "gift-" + step.id, value: gift.variant_id,
              checked: selection.has(String(gift.variant_id)), "data-ds-gift-choice": true,
              "data-focus-key": "gift-" + step.id + "-" + gift.variant_id
            }),
            gift.featured_image ? element("img", { src: safeURL(gift.featured_image), alt: "", loading: "lazy", width: 44, height: 44 }) : element("span"),
            element("span", {}, [gift.title, gift.variant_title && gift.variant_title !== "Default Title" ? element("small", {}, [gift.variant_title]) : null])
          ])
        )));
        form.append(element("button", { type: "submit", class: "ds-cart-button", disabled: !selection.size, "data-focus-key": "gift-add-" + step.id }, [strings.addGifts]));
        form.dataset.limit = limit;
      }
      return form;
    }));
  }

  renderCodes(cart) {
    const target = this.querySelector("[data-ds-codes]");
    if (!target) return;
    // Codes Shopify does not apply to this cart stay hidden until they do.
    const codes = [...new Map((cart.discount_codes || []).filter((entry) => entry.applicable).map((entry) => [codeKey(entry.code), entry.code])).values()];
    target.replaceChildren(...codes.map((code) => {
      const amount = discountAmount(code, this.config.discountAllocations);
      const icon = this.querySelector("[data-ds-discount-icon]")?.content.cloneNode(true);
      return element("span", { class: "ds-cart-discount-code" }, [
        icon ? element("span", { class: "ds-cart-discount-code__icon", "aria-hidden": "true" }, [icon]) : null,
        element("span", {}, [code.toLocaleUpperCase(this.config.locale || "de"), amount > 0 ? " (-" + this.money(amount) + ")" : ""]),
        element("button", { type: "button", "data-ds-remove-code": code, "data-focus-key": "remove-code-" + codeKey(code), "aria-label": this.config.strings.remove + " " + code }, ["×"])
      ]);
    }));
  }

  renderDiscountFeedback() {
    const node = this.querySelector("[data-ds-discount-feedback]");
    if (!node) return;
    const { message = "", kind = "success" } = this.discountFeedback || {};
    node.textContent = message;
    node.hidden = !message;
    node.dataset.kind = kind;
    node.setAttribute("role", kind === "error" ? "alert" : "status");
    this.querySelector('[data-focus-key="discount"]')?.setAttribute("aria-invalid", String(Boolean(message) && kind === "error"));
  }

  async applyDiscount(value) {
    this.error();
    this.discountFeedback = null;
    this.renderDiscountFeedback();
    try {
      await cartAdapter.setCode(value);
      this.config = readConfig();
      const applicable = cartAdapter.cart.discount_codes?.some((entry) => codeKey(entry.code) === codeKey(value) && entry.applicable);
      const matchingCampaign = this.config.campaigns?.some((campaign) => codeKey(campaign.code) === codeKey(value));
      if (applicable) {
        this.drafts.discount = "";
        const input = this.querySelector('[data-focus-key="discount"]');
        if (input) input.value = "";
        this.discountFeedback = { message: this.config.strings.codeSuccess, kind: "success" };
      } else {
        this.discountFeedback = { message: matchingCampaign ? this.config.strings.codePending : this.config.strings.codeInvalid, kind: matchingCampaign ? "pending" : "error" };
        // The service also warns non-UI callers. Keep this validation by the field.
        if (this.querySelector("[data-ds-error]")?.textContent === this.config.strings.codeInvalid) this.error();
      }
    } catch (error) {
      this.discountFeedback = { message: error.message || this.config.strings.error, kind: "error" };
    }
    this.renderDiscountFeedback();
  }

  async removeDiscount(code) {
    this.discountFeedback = null;
    this.renderDiscountFeedback();
    let removed = false;
    await this.perform(async () => {
      await cartAdapter.removeCode(code);
      removed = true;
    });
    if (removed) this.discountFeedback = { message: this.config.strings.codeRemoved, kind: "success" };
    this.renderDiscountFeedback();
  }

  async loadRecommendations(cart) {
    const target = this.querySelector("[data-ds-recommendations]");
    if (!target || !this.config.useRecommendations || !cart.items.length) return;
    const productId = cart.items.find((item) => !item.properties?._custom_gift_step)?.product_id;
    if (!productId) return;
    if (!this.recommendations.has(productId)) {
      this.recommendationRequest?.abort();
      this.recommendationRequest = new AbortController();
      const url = window.Shopify.routes.root + "recommendations/products.json?product_id=" + productId + "&limit=6&intent=related";
      const response = await fetch(url, { signal: this.recommendationRequest.signal });
      if (!response.ok) return;
      this.recommendations.set(productId, (await response.json()).products || []);
    }
    if (!target.isConnected) return;
    const existing = new Set(cart.items.map((item) => String(item.product_id)));
    const products = this.recommendations.get(productId).filter((product) => !existing.has(String(product.id)) && product.available && !product.requires_selling_plan);
    if (!products.length) return;
    const cards = products.map((product) => {
      const variant = product.variants.find((entry) => entry.available);
      if (!variant) return null;
      return element("article", { class: "ds-cart-upsell__card" }, [
        element("a", { href: safeURL(product.url) }, [product.featured_image ? element("img", { src: safeURL(product.featured_image), alt: product.title, width: 64, height: 72, loading: "lazy" }) : null]),
        element("div", {}, [
          element("a", { href: safeURL(product.url) }, [product.title]), element("p", {}, [this.money(variant.price)]),
          element("button", { type: "button", class: "ds-cart-button", "data-ds-add": variant.id, "aria-label": this.config.strings.add + " " + product.title }, [this.config.strings.add])
        ])
      ]);
    }).filter(Boolean);
    if (cards.length) target.replaceChildren(...cards);
  }

  onClick(event) {
    const button = event.target.closest("button, [data-ds-close]");
    if (!button || !this.contains(button)) return;
    if (button.hasAttribute("data-ds-reward-toggle")) {
      const marker = button.closest("[data-ds-reward]");
      if (this.activeReward === marker) this.closeReward();
      else this.showReward(marker);
      return;
    }
    if (button.hasAttribute("data-ds-close")) { this.hide(); return; }
    if (button.hasAttribute("data-ds-progress-toggle")) {
      this.drafts.progressExpanded = !this.drafts.progressExpanded;
      button.setAttribute("aria-expanded", String(this.drafts.progressExpanded));
      this.querySelector(".ds-progress-bar__message-details").hidden = !this.drafts.progressExpanded;
      return;
    }
    if (button.hasAttribute("data-ds-modal-close")) {
      const modal = button.closest("dialog-element");
      if (modal.querySelector("[data-ds-note]")) {
        this.drafts.note = cartAdapter.cart.note || "";
        modal.querySelector("textarea").value = this.drafts.note;
      }
      modal.hide(); return;
    }
    const row = button.closest("[data-ds-item]");
    if (row && button.hasAttribute("data-ds-delta")) {
      this.perform(() => cartAdapter.change(row.dataset.key, Number(button.dataset.dsDelta), { delta: true }));
    } else if (row && button.hasAttribute("data-ds-remove")) {
      this.perform(() => cartAdapter.change(row.dataset.key, 0));
    } else if (button.hasAttribute("data-ds-remove-code")) {
      this.removeDiscount(button.dataset.dsRemoveCode);
    } else if (button.hasAttribute("data-ds-add") && !button.disabled) {
      button.disabled = true;
      this.perform(() => cartAdapter.add({ items: [{ id: Number(button.dataset.dsAdd), quantity: 1 }] }, { initiator: this }))
        .finally(() => { button.disabled = false; });
    }
  }

  onChange(event) {
    const input = event.target;
    if (input.hasAttribute("data-ds-quantity") && input.validity.valid) {
      this.perform(() => cartAdapter.change(input.closest("[data-ds-item]").dataset.key, input.value));
    }
    if (input.hasAttribute("data-ds-gift-choice")) {
      const form = input.closest("form");
      let selected = [...form.querySelectorAll("input:checked")];
      if (selected.length > Number(form.dataset.limit)) { input.checked = false; selected = selected.filter((entry) => entry !== input); }
      this.drafts.choices[form.dataset.dsGiftForm] = selected.map((entry) => entry.value);
      form.querySelector('button[type="submit"]').disabled = !selected.length;
    }
  }

  onSubmit(event) {
    const form = event.target;
    if (form.hasAttribute("data-ds-checkout")) {
      if (cartAdapter.pending) event.preventDefault();
      return;
    }
    if (form.hasAttribute("data-ds-discount")) {
      event.preventDefault();
      const value = form.elements.discount.value.trim();
      if (!value || cartAdapter.pending) {
        this.busy();
        return;
      }
      this.applyDiscount(value);
    } else if (form.hasAttribute("data-ds-note")) {
      event.preventDefault();
      const note = form.elements.note.value;
      form.closest("dialog-element").hide();
      this.perform(() => cartAdapter.update({ note }));
    } else if (form.hasAttribute("data-ds-gift-form")) {
      event.preventDefault();
      const ids = [...form.querySelectorAll("input:checked")].map((input) => input.value);
      this.perform(() => cartAdapter.selectGifts(form.dataset.dsGiftForm, ids));
    }
  }
}

if (!customElements.get("ds-cart-drawer")) customElements.define("ds-cart-drawer", DSCartDrawer);
